'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { PdpClientLink } from 'links/pdp/client'
import { ColorSelectorPicker } from 'product-components/components/ColorSelector/ColorSelectorPicker/ColorSelectorPicker'
import { applyStylesIf, cx } from 'utils/cx'

import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import type { Color, FullProduct, Look } from 'pdp/src/types/FullProduct'
import { useColorList } from './hooks/useColorList'

import fukkuClasses from '@fukku/styles/classes.module.scss'
import styles from './ColorList.module.scss'

const COLOR_SELECTOR_TITLE_ID = 'product.colorsSelector'

export interface ColorListProps {
	readonly product: FullProduct
	readonly look: Look
	readonly priority?: boolean
	readonly location?: string
	readonly className?: string
	readonly 'data-testid'?: string
}

export const ColorList = ({
	product,
	look,
	location,
	className,
	priority = false,
	'data-testid': dataTestId,
}: ColorListProps) => {
	const { t } = useLabels()
	const { sendChangeColorEvent } = useAnalytics()
	const { colorToRender, selectedColor, isColorListReduced, parsedProduct } =
		useColorList({ product, location })

	return (
		<div className={className} data-testid={dataTestId}>
			<p
				id={`${dataTestId}.${COLOR_SELECTOR_TITLE_ID}`}
				className={fukkuClasses.srOnly}
			>
				{t('accessibility.pdp.addColor.selector.noColor')}
			</p>
			<ul
				className={styles.colorsList}
				aria-describedby={`${dataTestId}.${COLOR_SELECTOR_TITLE_ID}`}
			>
				{colorToRender.map((color: Color) => {
					const isSelected = color.id === selectedColor.id

					const selectedLabel: string = replacePlaceholders(
						isSelected
							? t('accessibility.pdp.addColor.selector.withColor')
							: t('accessibility.pdp.addColor.colorName.text'),
						{ 1: color.label }
					)

					return (
						<li
							key={color.id}
							data-testid={dataTestIds['pdp.colorSelector.link.fn'](color.id)}
							className={cx(
								styles.color,
								applyStylesIf(isColorListReduced, styles.reduced)
							)}
						>
							{isSelected ? (
								<ColorSelectorPicker
									outOfStock={!color.isSellable}
									imageAlt={selectedLabel}
									imageSrc={color.bulletImg}
									priority={priority}
									selected
								/>
							) : (
								<PdpClientLink
									product={parsedProduct}
									colorId={color.id}
									lookId={look.id}
									location={location}
									linkProps={{
										onClick: sendChangeColorEvent,
										className: styles.link,
									}}
								>
									<ColorSelectorPicker
										outOfStock={!color.isSellable}
										imageAlt={selectedLabel}
										priority={priority}
										imageSrc={color.bulletImg}
									/>
								</PdpClientLink>
							)}
						</li>
					)
				})}
			</ul>
		</div>
	)
}
